import React, { useState } from 'react';

import { filterMatches, matchesOfPlayer } from './scoring-match-query';
import ScoringMatchCard from './scoring-match-card';
import ScoringMatchModal from './scoring-match-modal';
import ScoringPlayerMatchesModal from './scoring-player-matches-modal';
import ScoringSearchBox from './scoring-search-box';
import ScoringSectionLine, { MATCH_UNITS, PAIR_UNITS, PLAYER_UNITS, countLabel } from './scoring-section-line';
import ScoringStandings from './scoring-standings';
import TiebreakNote from './tiebreak-note';
import { orderedRuns, runLabel, runsOfMatches, type RunUnit } from './scoring-runs';
import { groupFormatLabel, usesPointSystem, type MatchItem, type ScoringGroup } from './scoring-types';

interface Props {
  group: ScoringGroup;
  matches: MatchItem[];
  token: string;
  canSubmit: boolean;
  showBack: boolean;
  doubles?: boolean;
  runUnit?: RunUnit;
  big?: boolean;
  onBack: () => void;
  onSaved: () => void;
}

export const ScoringGroupView: React.FC<Props> = ({
  group,
  matches,
  token,
  canSubmit,
  showBack,
  doubles = false,
  runUnit = 'runda',
  big = false,
  onBack,
  onSaved,
}) => {
  // Wynik wpisuje się w modalu — na telefonie formularz rozwijany w liście
  // spychał mecz poza ekran, a zapis i tak zamyka okno.
  const [openId, setOpenId] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [pickedPlayerId, setPickedPlayerId] = useState<number | null>(null);

  const visibleMatches = filterMatches(matches, query);
  const runs = orderedRuns(runsOfMatches(visibleMatches), runUnit);
  const pickedPlayer =
    group.standings.find((row) => row.leaguePlayerId === pickedPlayerId) ?? null;
  const openMatch = matches.find((match) => match.id === openId) ?? null;

  function matchCard(match: MatchItem): JSX.Element {
    return (
      <ScoringMatchCard
        key={match.id}
        match={match}
        canSubmit={canSubmit}
        scoring={group.scoring}
        onOpen={() => setOpenId(match.id)}
      />
    );
  }

  function handleSaved(): void {
    setOpenId(null);
    onSaved();
  }

  return (
    <section className="scoring-group">
      <header className="scoring-group__header" data-testid="scoring-group-header">
        {showBack && (
          <button
            type="button"
            className="scoring-group__back"
            data-testid="scoring-back-to-groups"
            onClick={onBack}
          >
            ← Grupy
          </button>
        )}
        {group.name !== null && <h2 className="scoring-group__name">Grupa {group.name}</h2>}
        <span className="scoring-group__rules" data-testid="scoring-group-rules">
          {groupFormatLabel(group)}
        </span>
      </header>

      <ScoringSectionLine title="Tabela" count={countLabel(group.standings.length, doubles ? PAIR_UNITS : PLAYER_UNITS)}>
        {!big && (
          <TiebreakNote
            hint={group.tiebreak?.hint}
            steps={group.tiebreak?.steps}
            formatLabel={groupFormatLabel(group)}
            testId="scoring-group-note"
          />
        )}
      </ScoringSectionLine>

      <ScoringStandings
        rows={group.standings}
        showPoints={usesPointSystem(group)}
        doubles={doubles}
        tiebreakHint={group.tiebreak?.hint}
        restingRunPoints={group.restingRunPoints ?? null}
        compact={big}
        onPickPlayer={(row) => setPickedPlayerId(row.leaguePlayerId)}
      />

      <ScoringSectionLine title="Mecze" count={countLabel(matches.length, MATCH_UNITS)}>
        <ScoringSearchBox value={query} onChange={setQuery} />
      </ScoringSectionLine>

      {!big && (
        <p className="scoring-hint">
          Kliknij mecz — zobaczysz sety i zmianę rankingu. Kliknij zawodnika w tabeli —
          zobaczysz jego ranking i mecze w tej grupie.
        </p>
      )}

      {visibleMatches.length === 0 && query.trim() !== '' && (
        <p className="scoring-no-matches" data-testid="scoring-search-empty">
          Brak meczów dla „{query}".
        </p>
      )}

      <ul className="scoring-matches" data-testid="scoring-matches">
        {runs === null
          ? visibleMatches.map(matchCard)
          : runs.map((run) => (
              <React.Fragment key={run.number}>
                <li className="scoring-run-head" data-testid={`scoring-run-${run.number}`}>
                  {runLabel(run, runUnit)}
                </li>
                {run.matches.map(matchCard)}
              </React.Fragment>
            ))}
      </ul>

      {openMatch !== null && (
        <ScoringMatchModal
          match={openMatch}
          token={token}
          canSubmit={canSubmit}
          scoring={group.scoring}
          formatLabel={groupFormatLabel(group)}
          big={big}
          onClose={() => setOpenId(null)}
          onSaved={handleSaved}
        />
      )}

      {pickedPlayer !== null && (
        <ScoringPlayerMatchesModal
          player={pickedPlayer}
          matches={matchesOfPlayer(matches, pickedPlayer.leaguePlayerId)}
          showPoints={usesPointSystem(group)}
          scoring={group.scoring}
          doubles={doubles}
          formatLabel={groupFormatLabel(group)}
          restingRunPoints={group.restingRunPoints ?? null}
          entry={{ token, canSubmit, onSaved: handleSaved }}
          big={big}
          onClose={() => setPickedPlayerId(null)}
        />
      )}
    </section>
  );
};

export default ScoringGroupView;
